import React, { useState } from "react";
import { Modal, FormGroup, Button, Label, Spinner } from "reactstrap";
import CloseIcon from "mdi-react/CloseIcon";
import CheckIcon from "mdi-react/CheckIcon";
import { Form, Field } from "react-final-form";
import { dateToUSFormat } from "@/shared/helpers";

/**
 * ICOV3-1701: perRequestMode lets each item in awardApprovalParticipants get
 * an independent Approve/Deny decision (own rejection note) instead of one
 * shared decision applied to the whole list — needed when a single
 * participant has multiple pending requests awaiting the same manager.
 * Default false: PendingAwardApprovals.jsx's existing bulk-select flow does
 * not pass this prop and is unaffected.
 */
const AwardApprovalPopup = ({
  isOpen,
  setOpen,
  toggle,
  awardApprovalParticipants = [],
  statusName,
  rejection_notes,
  isShow = false,
  onSubmit,
  loading,
  perRequestMode = false,
  onDecideOne,
  decidingId = null,
  programName,
}) => {
  const [perRequestNotes, setPerRequestNotes] = useState({});
  const totalAmount = awardApprovalParticipants?.reduce(
    (totalAmount, award) => totalAmount + award.amount,
    0
  );

  if (perRequestMode) {
    return (
      <Modal
        className="program-settings manager-program-settings-modal modal-lg pending-award-modal"
        isOpen={isOpen}
        toggle={toggle}
      >
        <div className="manager-program-settings-modal__layout">
          <div className="manager-program-settings-modal__main">
            <div className="manager-program-settings-modal__header pending-award-modal__header">
              <div className="manager-program-settings-modal__header-copy">
                <h3 className="manager-program-settings-modal__title">
                  Pending Awards ({awardApprovalParticipants.length})
                </h3>
              </div>
              <button
                type="button"
                className="pending-award-modal__close"
                onClick={toggle}
                aria-label="Close"
              >
                <CloseIcon size={20} />
              </button>
            </div>
            <div className="manager-program-settings-modal__body pending-award-modal__body">
              {awardApprovalParticipants.map((request) => {
                const initials = `${request.participant_first_name?.[0] || ""}${
                  request.participant_last_name?.[0] || ""
                }`.toUpperCase();
                const requestorName = [request.requestor_first_name, request.requestor_last_name]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <div key={request.id} className="pending-award-modal__card">
                    <div className="pending-award-modal__card-head">
                      <div className="pending-award-modal__avatar">{initials || "?"}</div>
                      <div className="pending-award-modal__identity">
                        <span className="pending-award-modal__name">
                          {request.participant_first_name} {request.participant_last_name}
                        </span>
                        {request.event_name && (
                          <span className="pending-award-modal__event">{request.event_name}</span>
                        )}
                      </div>
                      <span className="pending-award-modal__amount">${request.amount}</span>
                    </div>

                    {(requestorName || request.created_at || programName) && (
                      <div className="pending-award-modal__meta">
                        {requestorName && (
                          <div className="pending-award-modal__meta-row">
                            <span className="pending-award-modal__meta-label">Requested by</span>
                            <span className="pending-award-modal__meta-value">{requestorName}</span>
                          </div>
                        )}
                        {request.created_at && (
                          <div className="pending-award-modal__meta-row">
                            <span className="pending-award-modal__meta-label">Submitted</span>
                            <span className="pending-award-modal__meta-value">
                              {dateToUSFormat(request.created_at)}
                            </span>
                          </div>
                        )}
                        {programName && (
                          <div className="pending-award-modal__meta-row">
                            <span className="pending-award-modal__meta-label">Source</span>
                            <span className="pending-award-modal__meta-value">{programName}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {request.message && (
                      <blockquote className="pending-award-modal__message">{request.message}</blockquote>
                    )}

                    <FormGroup className="pending-award-modal__notes">
                      <Label>Approval notes (required to deny)</Label>
                      <textarea
                        placeholder="Explain why this award is being denied"
                        value={perRequestNotes[request.id] || ""}
                        onChange={(e) =>
                          setPerRequestNotes((prev) => ({ ...prev, [request.id]: e.target.value }))
                        }
                      />
                    </FormGroup>

                    <div className="pending-award-modal__actions">
                      {loading && decidingId === request.id ? (
                        <Spinner size="sm" color="primary">
                          Loading...
                        </Spinner>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="pending-award-modal__btn pending-award-modal__btn--approve"
                            disabled={loading}
                            onClick={() => onDecideOne(request, "approved", perRequestNotes[request.id])}
                          >
                            <CheckIcon size={16} /> Approve
                          </button>
                          <button
                            type="button"
                            className="pending-award-modal__btn pending-award-modal__btn--deny"
                            disabled={loading}
                            onClick={() => onDecideOne(request, "reject", perRequestNotes[request.id])}
                          >
                            <CloseIcon size={16} /> Deny
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      className={`program-settings modal-lg padded`}
      isOpen={isOpen}
      toggle={toggle}
    >
      <div className="close cursor-pointer">
        <CloseIcon onClick={toggle} size={30} />
      </div>
      <div className="right p-2">
        <h4 className="bg-secondary rounded text-white p-2">
          {statusName == "approved"
            ? `Are you sure you want to Approve ${awardApprovalParticipants.length}
          pending Awards?`
            : `Are you sure you want to Deny ${awardApprovalParticipants.length}
          pending Awards?`}{" "}
        </h4>
        {!isShow && (
          <div>
            <div className="d-flex m-1">
              <h5>Selected Users: </h5> <span>(Name, Amount)</span>
            </div>
            <div>
              {awardApprovalParticipants?.map((participant, i) => {
                return (
                  <div key={participant.id}>
                    <div className="m-3">
                      <ul>
                        <li>
                          {participant.participant_first_name}{" "}
                          {participant.participant_last_name}, $
                          {participant.amount}
                        </li>
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
            <h6
              className="m-1 mt-2"
              style={{ fontSize: "18px", fontWeight: "bold" }}
            >
              Total Amount: $ {totalAmount}
            </h6>
          </div>
        )}

        <Form
          onSubmit={onSubmit}
          initialValues={{ rejection_note: rejection_notes }}
        >
          {({ handleSubmit, form, submitting, pristine, values }) => {
            // console.log(values)
            return (
              <form
                className="form d-flex flex-column justify-content-evenly mt-4 p-2"
                onSubmit={handleSubmit}
              >
                {statusName === "reject" && (
                  <>
                    <Label className="text-bold">Rejection Note:</Label>
                    <Field name="rejection_note">
                      {({ input, meta }) => (
                        <FormGroup>
                          <textarea {...input} className="w-50" />
                          {meta.touched && meta.error && (
                            <span className="text-danger">{meta.error}</span>
                          )}
                        </FormGroup>
                      )}
                    </Field>
                  </>
                )}
                <hr />
                <div className="d-flex justify-content-end">
                  {!loading ? (
                    <Button color="primary" type="submit">
                      Save
                    </Button>
                  ) : (
                    <Spinner size="md" color="primary">
                      Loading...
                    </Spinner>
                  )}
                </div>
              </form>
            );
          }}
        </Form>
      </div>
    </Modal>
  );
};

export default AwardApprovalPopup;
